import React, { memo, useEffect, useState } from 'react';
import {
  Grid,
  Divider,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { FormLayout, FormQuestion, FormHeader } from '../Common';
import { UI_CONFIG, QUESTION_TYPE } from '../../constants/Constants';
import { userConstants } from '../../locale/userTexts';
import { RenderQuestionType } from '../RespondentForm';

const RespondentFormEditable = ({
  questionnaire,
  questions,
  existingResponses,
  onSubmit,
  isLoading,
  showHeader,
  language,
}: any) => {
  const [questionsData, setQuestionsData] = useState<any[]>([]);
  const [isValidResponse, setIsValidResponse] = useState(true);

  useEffect(() => {
    if (questions && existingResponses) {
      const responseMap: any = {};
      existingResponses.forEach((r: any) => {
        responseMap[r.questionId] = r.response;
      });
      const prefilled = questions.map((q: any) => {
        const resp = responseMap[q.id];
        if (!resp) {
          return {
            ...q,
            response: q.type === QUESTION_TYPE.SINGLE_SELECT ? -1 : null,
          };
        }
        const value = resp[q.type];
        let responseIndex: any = null;
        if (q.type === QUESTION_TYPE.SINGLE_SELECT) {
          responseIndex = q.options.findIndex(
            (opt: any) => opt.option === value?.option,
          );
        } else if (q.type === QUESTION_TYPE.RATING) {
          responseIndex = value?.value !== undefined ? Number(value.value) : null;
        } else if (q.type === QUESTION_TYPE.OPEN_ENDED) {
          responseIndex = value || '';
        }
        return {
          ...q,
          response: responseIndex,
          answer: value?.responseText || '',
          commentText: resp?.comment?.responseText || '',
        };
      });
      setQuestionsData(prefilled);
    }
  }, [questions, existingResponses]);

  useEffect(() => {
    let isValid = true;
    questionsData.forEach((d) => {
      if (
        d.isRequired
        && (d.response === -1 || d.response === null || d.response === '')
      ) {
        isValid = false;
      }
    });
    setIsValidResponse(isValid);
  }, [questionsData]);

  const handleRadioClick = (index: number, event: any) => {
    const { value } = event.target;
    const updated = [...questionsData];
    if (Number(value) === updated[index].response) {
      updated[index].response = -1;
    } else {
      updated[index].response = Number(value);
    }
    setQuestionsData(updated);
  };

  const onOtherChange = (index: number, event: any) => {
    const { value, name } = event.target;
    const updated = [...questionsData];
    updated[index] = {
      ...updated[index],
      [name]: value,
    };
    setQuestionsData(updated);
  };

  const getValue = (d: any) => {
    switch (d.type) {
      case QUESTION_TYPE.SINGLE_SELECT:
        return d.options[d.response];
      case QUESTION_TYPE.OPEN_ENDED:
        return d.response;
      case QUESTION_TYPE.RATING:
        return typeof d.response === 'number'
          ? d.options[d.response - 1]
          : null;
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    const responses: any[] = [];

    questionsData.forEach((d) => {
      if (d.response !== -1) {
        responses.push({
          questionId: d.id,
          response: {
            [d.type]: getValue(d),
          },
          tags: d.tags,
        });
      }
    });

    await onSubmit(responses);
  };

  return isLoading ? (
    <div className="loaderCont">
      <CircularProgress size={UI_CONFIG.loaderSize} />
    </div>
  ) : (
    <div className="newQuestCont">
      {showHeader && (
        <div className="formSect">
          <FormHeader data={questionnaire} />
        </div>
      )}

      <div className="formSect">
        <FormLayout>
          <form>
            {questionsData.map((d, index) => (
              <React.Fragment key={d.id}>
                <Grid container spacing={2}>
                  <FormQuestion
                    index={index}
                    questionText={d.questionText}
                    description={d.description}
                    imageUrl={d.imageUrl}
                    isRequired={d.isRequired}
                  />

                  <Grid item xs={12}>
                    <RenderQuestionType
                      type={d.type}
                      options={d.options}
                      response={d.response}
                      onChange={(e: any) => {
                        let value;
                        if (d.type === QUESTION_TYPE.OPEN_ENDED) {
                          value = e.target.value;
                        } else if (d.type === QUESTION_TYPE.RATING) {
                          value = typeof e === 'number' ? e : e?.target?.value;
                        } else {
                          value = Number(e.target.value);
                        }
                        const updated = [...questionsData];
                        updated[index] = {
                          ...updated[index],
                          response: value,
                        };
                        setQuestionsData(updated);
                      }}
                      onOtherChange={(e: any) => onOtherChange(index, e)}
                      handleRadioClick={(e: any) => handleRadioClick(index, e)}
                      language={language}
                      commentType={d.commentType}
                      commentTypeData={d.comment}
                    />
                  </Grid>
                </Grid>

                <Divider style={{ margin: '10px 0' }} />
              </React.Fragment>
            ))}

            <Button
              type="button"
              variant="outlined"
              className="themeBtn"
              disabled={!isValidResponse}
              onClick={handleSubmit}
            >
              {userConstants(language).submitButtonInUpperCase}
            </Button>
          </form>
        </FormLayout>
      </div>
    </div>
  );
};

export default memo(RespondentFormEditable);
